import React, { useState, useEffect, useCallback } from 'react';
import '../../styles/PayoutModal.css';
import { API_BASE_URL } from '../../config';
import dayjs from 'dayjs';

function PayoutModal({ isOpen, onClose, brandId, userId, authKey, onSuccess }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [payoutRequests, setPayoutRequests] = useState([]);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [payoutSuccess, setPayoutSuccess] = useState(false);

  const fetchPayoutRequests = useCallback(async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${API_BASE_URL}/brands/ledger/payout/get`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          brand_id: brandId,
          user_id: userId,
          auth_key: authKey
        })
      });
      
      const data = await response.json();
      if (data.status === 200) {
        setPayoutRequests(data.data);
      } else {
        throw new Error(data.message || 'Failed to fetch payout requests');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [brandId, userId, authKey]);

  useEffect(() => {
    if (isOpen) {
      fetchPayoutRequests();
    }
  }, [isOpen, fetchPayoutRequests]);

  const handleCreatePayout = async () => {
    try {
      setLoading(true);
      setError('');
      setPayoutSuccess(false);

      const response = await fetch(`${API_BASE_URL}/brands/ledger/payout/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          brand_id: brandId,
          user_id: userId,
          auth_key: authKey
        })
      });

      const data = await response.json();
      
      if (data.status === 200) {
        setPayoutSuccess(true);
        fetchPayoutRequests(); // Refresh the list
        setTimeout(() => {
          setShowConfirmation(false);
          if (onSuccess) {
            onSuccess();
          }
        }, 2000);
      } else {
        let errorMessage;
        switch (data.status) {
          case 400:
            errorMessage = data.message;
            break;
          case 401:
            errorMessage = 'Authentication failed. Please try logging in again.';
            break;
          case 403:
            errorMessage = 'You do not have permission to perform this action.';
            break;
          case 500:
            errorMessage = 'Server error occurred. Please try again later.';
            break;
          default:
            errorMessage = data.message || 'Failed to create payout request';
        }
        throw new Error(errorMessage);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return dayjs(dateString).format('MMM DD, YYYY HH:mm:ss');
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount);
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{padding: '0px'}}>
        <div className="modal-header">
          Payout Requests
          <button className="close-button" onClick={onClose}>&times;</button>
        </div>

        {loading ? (
          <div className="modal-loading">
            <div className="loader"></div>
            <p>Loading payout requests...</p>
          </div>
        ) : error ? (
          <div className="modal-error">
            <p>{error}</p>
            <button onClick={fetchPayoutRequests}>Retry</button>
          </div>
        ) : (
          <div className="modal-body">
            <div className="payout-requests-section">
              <h3>Previous Payout Requests</h3>
              {payoutRequests.length > 0 ? (
                <div className="payout-requests-list">
                  {payoutRequests.map((request) => (
                    <div key={request.payout_id} className="payout-request-item">
                      <div className="payout-info">
                        <div className="payout-amount">
                          {formatAmount(request.amount)}
                        </div>
                        <div className="payout-status">
                          <span className={`status-badge ${request.status}`}>
                            {request.status}
                          </span>
                        </div>
                      </div>
                      <div className="payout-details">
                        <div className="payout-date">
                          Requested on: {formatDate(request.created_at)}
                        </div>
                        <div className="payout-id">
                          ID: {request.payout_id}
                        </div>
                        {request.status === "approved" && (
                          <div className="payout-id" style={{marginTop: '5px'}}>
                            UTR: {request.utr} | Notes: {request.notes}
                          </div>
                        )}
                        {request.status === "rejected" && (
                          <div className="payout-id" style={{marginTop: '5px'}}>
                            Notes: {request.notes}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="no-requests">No payout requests found</p>
              )}
            </div>

            {payoutSuccess && (
              <div className="success-message">
                Payout request created successfully!
              </div>
            )}

            <div className="modal-actions">
              <button 
                className="payout-button"
                onClick={() => setShowConfirmation(true)}
                disabled={loading}
              >
                Request New Payout
              </button>
            </div>

            {showConfirmation && (
              <div className="confirmation-modal">
                <div className="confirmation-content">
                  <h3>Confirm Payout Request</h3>
                  <p>Are you sure you want to request a payout? This will create a payout request for your entire available balance.</p>
                  <p className="note">Note: Minimum payout amount is ₹1,000</p>
                  <div className="confirmation-actions">
                    <button 
                      className="confirm-button"
                      onClick={handleCreatePayout}
                      disabled={loading}
                    >
                      {loading ? 'Processing...' : 'Confirm'}
                    </button>
                    <button 
                      className="cancel-button"
                      onClick={() => setShowConfirmation(false)}
                      disabled={loading}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default PayoutModal; 